import React, { useMemo } from 'react';

/**
 * DeadlineTimeline Component
 * 
 * Visual timeline showing deadlines grouped by:
 * - Overdue
 * - Today
 * - Tomorrow
 * - This Week (next 5 days)
 * - Next Week
 */
const DeadlineTimeline = ({ deadlines = [], loading, onItemClick }) => {
    // Group deadlines by time period
    const grouped = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        const dayAfterTomorrow = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);
        const thisWeekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        const nextWeekEnd = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);

        const groups = {
            overdue: [],
            today: [],
            tomorrow: [],
            thisWeek: [],
            nextWeek: [],
            later: []
        };

        (deadlines || []).forEach(d => {
            if (!d.date) {
                groups.overdue.push(d); // No date = urgent
                return;
            }

            const date = new Date(d.date);

            if (date < today) {
                groups.overdue.push(d);
            } else if (date < tomorrow) {
                groups.today.push(d);
            } else if (date < dayAfterTomorrow) {
                groups.tomorrow.push(d);
            } else if (date < thisWeekEnd) {
                groups.thisWeek.push(d);
            } else if (date < nextWeekEnd) {
                groups.nextWeek.push(d);
            } else {
                groups.later.push(d);
            }
        });

        return groups;
    }, [deadlines]);

    const getTypeIcon = (type) => {
        switch (type) {
            case 'university_application':
            case 'application':
                return '🎓';
            case 'visa':
            case 'visa_application':
                return '✈️';
            case 'agreement':
                return '📄';
            case 'case':
                return '📁';
            default:
                return '✓';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'university_application':
            case 'application':
                return 'bg-blue-900/40 border-blue-700 text-blue-300';
            case 'visa':
            case 'visa_application':
                return 'bg-purple-900/40 border-purple-700 text-purple-300';
            case 'agreement':
                return 'bg-green-900/40 border-green-700 text-green-300';
            case 'case':
                return 'bg-orange-900/40 border-orange-700 text-orange-300';
            default:
                return 'bg-gray-700 border-gray-600 text-gray-300';
        }
    };

    const TimelineSection = ({ title, items, color, icon, showEmpty = false }) => {
        if (items.length === 0 && !showEmpty) return null;

        return (
            <div className="mb-4">
                <div className={`flex items-center gap-2 mb-2 text-sm font-medium ${color}`}>
                    <span>{icon}</span>
                    <span>{title}</span>
                    <span className="px-1.5 py-0.5 bg-gray-700 rounded text-xs">{items.length}</span>
                </div>
                {items.length > 0 ? (
                    <div className="space-y-2 ml-6 border-l-2 border-gray-700 pl-4">
                        {items.map((item, idx) => (
                            <div
                                key={`${item.type}-${item.id}-${idx}`}
                                onClick={() => onItemClick && onItemClick(item)}
                                className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer hover:bg-gray-700/50 transition-colors ${getTypeColor(item.type)}`}
                            >
                                <span className="text-lg">{getTypeIcon(item.type)}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{item.title}</div>
                                    {item.subtitle && <div className="text-[10px] opacity-75">{item.subtitle}</div>}
                                </div>
                                {item.date && (
                                    <div className="text-xs whitespace-nowrap opacity-75">
                                        {new Date(item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="ml-6 border-l-2 border-gray-700 pl-4 py-2 text-gray-500 text-xs">
                        No deadlines
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-4 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    <div className="h-8 bg-gray-700 rounded"></div>
                    <div className="h-8 bg-gray-700 rounded"></div>
                    <div className="h-8 bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    const totalCount = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);

    return (
        <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden h-full flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
                <h2 className="text-white font-bold uppercase tracking-wider flex items-center gap-2 text-sm">
                    📅 Deadline Timeline
                    <span className="text-xs bg-gray-700 px-2 py-0.5 rounded-full text-gray-400">
                        {totalCount}
                    </span>
                </h2>
            </div>

            {/* Timeline Content */}
            <div className="flex-1 overflow-y-auto p-4">
                {totalCount === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                        <div className="text-4xl mb-3">🎉</div>
                        <div>No upcoming deadlines</div>
                    </div>
                ) : (
                    <>
                        <TimelineSection
                            title="Overdue"
                            items={grouped.overdue}
                            color="text-red-400"
                            icon="🔴"
                        />
                        <TimelineSection
                            title="Today"
                            items={grouped.today}
                            color="text-yellow-400"
                            icon="⚡"
                            showEmpty={grouped.overdue.length === 0}
                        />
                        <TimelineSection
                            title="Tomorrow"
                            items={grouped.tomorrow}
                            color="text-orange-400"
                            icon="🌅"
                        />
                        <TimelineSection
                            title="This Week"
                            items={grouped.thisWeek}
                            color="text-blue-400"
                            icon="📆"
                        />
                        <TimelineSection
                            title="Next Week"
                            items={grouped.nextWeek}
                            color="text-gray-400"
                            icon="📅"
                        />
                        {grouped.later.length > 0 && (
                            <TimelineSection
                                title="Later"
                                items={grouped.later}
                                color="text-gray-500"
                                icon="⏳"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DeadlineTimeline;
